import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Shield, Smartphone } from "lucide-react";
import { ApiError, api } from "../lib/api";
import { useAuth } from "../stores/auth";
import { toast } from "../stores/toast";
import { Button, Input, SettingsCard, Toggle } from "../components/ui";

export function ProfileSecurity() {
  const queryClient = useQueryClient();
  const fetchUserSecurity = useAuth((s) => s.fetchUserSecurity);
  const userSecurity = useAuth((s) => s.userSecurity);

  const [busy, setBusy] = useState(false);
  const [totp, setTotp] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [pinForm, setPinForm] = useState({ password: "", pin: "", confirm: "" });

  useEffect(() => {
    void fetchUserSecurity();
  }, [fetchUserSecurity]);

  async function toggleEmailMfa(enabled: boolean) {
    setBusy(true);
    try {
      await api(enabled ? "/auth/mfa/email/enable" : "/auth/mfa/email/disable", { body: {} });
      await fetchUserSecurity();
      toast("success", enabled ? "Email 2FA enabled" : "Email 2FA disabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't update 2FA");
    } finally {
      setBusy(false);
    }
  }

  async function startTotp() {
    setBusy(true);
    try {
      const data = await api<{ secret: string; otpauthUrl: string }>("/auth/mfa/totp/enroll", { body: {} });
      setTotp(data);
      setTotpCode("");
      toast("info", "Scan the QR code or enter the secret in your authenticator app");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't start enrollment");
    } finally {
      setBusy(false);
    }
  }

  async function confirmTotp() {
    if (!totpCode.trim()) {
      toast("error", "Enter the code from your authenticator app");
      return;
    }
    setBusy(true);
    try {
      await api("/auth/mfa/totp/confirm", { body: { code: totpCode.trim() } });
      setTotp(null);
      setTotpCode("");
      await fetchUserSecurity();
      toast("success", "Authenticator 2FA enabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  }

  async function disableTotp() {
    setBusy(true);
    try {
      await api("/auth/mfa/totp/disable", { body: {} });
      setTotp(null);
      await fetchUserSecurity();
      toast("success", "Authenticator 2FA disabled");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't disable 2FA");
    } finally {
      setBusy(false);
    }
  }

  async function setPin(e: React.FormEvent) {
    e.preventDefault();
    if (pinForm.pin.length !== 6) {
      toast("error", "PIN must be 6 digits");
      return;
    }
    if (pinForm.pin !== pinForm.confirm) {
      toast("error", "PINs do not match");
      return;
    }
    setBusy(true);
    try {
      await api("/auth/pin/set", { body: { password: pinForm.password, pin: pinForm.pin } });
      setPinForm({ password: "", pin: "", confirm: "" });
      await fetchUserSecurity();
      void queryClient.invalidateQueries();
      toast("success", "PIN saved");
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Couldn't save PIN");
    } finally {
      setBusy(false);
    }
  }

  if (!userSecurity) {
    return <p className="text-sm text-white/50">Loading security settings…</p>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <div>
        <h1 className="font-display text-lg font-bold">Security</h1>
        <p className="mt-0.5 text-xs text-white/50">Manage two-factor authentication and your PIN.</p>
      </div>

      {(userSecurity.mfaEmailAllowed || userSecurity.mfaTotpAllowed) && (
        <SettingsCard title="Two-factor authentication" description="Extra verification when you sign in.">
          {userSecurity.mfaEmailAllowed && (
            <div className="mb-4">
              <Toggle
                checked={userSecurity.mfaEmailEnabled}
                disabled={busy}
                onChange={(v) => void toggleEmailMfa(v)}
                label="Email verification codes"
              />
            </div>
          )}
          {userSecurity.mfaTotpAllowed && (
            <div className="space-y-3">
              {userSecurity.totpEnabled ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm text-white/85">
                    <Smartphone size={16} className="text-white/50" />
                    Authenticator app connected
                  </div>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => void disableTotp()}>
                    Disable
                  </Button>
                </div>
              ) : totp ? (
                <div className="space-y-3">
                  <p className="text-xs text-white/60">
                    Add this secret to your authenticator app, then enter a code to confirm.
                  </p>
                  <code className="block break-all rounded-lg bg-white/5 px-3 py-2 text-xs text-white/80">
                    {totp.secret}
                  </code>
                  <Input
                    label="Authenticator code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="6-digit code"
                    autoComplete="one-time-code"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={() => setTotp(null)}>
                      Cancel
                    </Button>
                    <Button size="sm" disabled={busy} onClick={() => void confirmTotp()}>
                      Confirm
                    </Button>
                  </div>
                </div>
              ) : (
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void startTotp()}>
                  <Smartphone size={14} className="mr-1.5 inline" />
                  Set up authenticator app
                </Button>
              )}
            </div>
          )}
        </SettingsCard>
      )}

      {userSecurity.pinEnabled && (
        <SettingsCard
          title="PIN login"
          description="Use a 6-digit PIN to unlock after session timeout."
          footer={
            <form className="flex justify-end gap-2" onSubmit={setPin}>
              <Button type="submit" size="sm" disabled={busy}>
                Save
              </Button>
            </form>
          }
        >
          <div className="space-y-3">
            {userSecurity.pinSet && (
              <p className="flex items-center gap-2 text-xs text-emerald-400/90">
                <Shield size={14} />
                PIN is set
              </p>
            )}
            <Input
              type="password"
              label="Current password"
              autoComplete="current-password"
              value={pinForm.password}
              onChange={(e) => setPinForm((p) => ({ ...p, password: e.target.value }))}
            />
            <Input
              type="password"
              inputMode="numeric"
              label="New PIN"
              maxLength={6}
              autoComplete="off"
              value={pinForm.pin}
              onChange={(e) =>
                setPinForm((p) => ({ ...p, pin: e.target.value.replace(/\D/g, "").slice(0, 6) }))
              }
            />
            <Input
              type="password"
              inputMode="numeric"
              label="Confirm PIN"
              maxLength={6}
              autoComplete="off"
              value={pinForm.confirm}
              onChange={(e) =>
                setPinForm((p) => ({ ...p, confirm: e.target.value.replace(/\D/g, "").slice(0, 6) }))
              }
            />
          </div>
        </SettingsCard>
      )}
    </div>
  );
}
